/**
 * ScoreHero — Data Readiness Score display.
 *
 * UX Spec Section 5.2.1 / Story 1.4 AC#1.
 *
 * Variants: standard (48px centered), compact (36px horizontal), stewardship (+ trend).
 * States: streaming, complete, paused, error, empty (hidden until first table).
 */

import { useEffect, useRef, useState } from "react";
import {
  AlertCircle,
  CheckCircle2,
  Loader2,
  Pause,
  TrendingDown,
  TrendingUp,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ScoreHeroState =
  | "streaming"
  | "complete"
  | "paused"
  | "error"
  | "empty";
export type ScoreHeroVariant = "standard" | "compact" | "stewardship";

export type SeverityLevel =
  | "excellent"
  | "good"
  | "moderate"
  | "poor"
  | "warning"
  | "critical";

export interface ScoreHeroProps {
  score: number;
  tablesProfiled: number;
  totalTables: number;
  state?: ScoreHeroState;
  variant?: ScoreHeroVariant;
  /** For stewardship variant: score delta from previous run */
  trendDelta?: number;
  /** Optional label rendered above the score (standard/stewardship variants)
   * so first-time users know what the number represents. */
  title?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function severityFromScore(score: number): SeverityLevel {
  if (score >= 90) return "excellent";
  if (score >= 75) return "good";
  if (score >= 60) return "moderate";
  if (score >= 40) return "poor";
  return "critical";
}

const SEVERITY_CONFIG: Record<
  SeverityLevel,
  { label: string; icon: string; color: string }
> = {
  excellent: {
    label: "Excellent",
    icon: "■",
    color: "var(--readiness-excellent)",
  },
  good: { label: "Good", icon: "✓", color: "var(--readiness-good)" },
  moderate: {
    label: "Moderate",
    icon: "▲",
    color: "var(--readiness-moderate)",
  },
  poor: { label: "Poor", icon: "◆", color: "var(--readiness-poor)" },
  warning: { label: "Warning", icon: "▲", color: "var(--readiness-warning)" },
  critical: {
    label: "Critical",
    icon: "●",
    color: "var(--readiness-critical)",
  },
};

// ---------------------------------------------------------------------------
// Animated number — eases the displayed score toward its target instead of
// snapping instantly. On fast profiling runs (small datasets can finish in
// low single-digit seconds) the backend may only deliver one or two score
// updates before completion; without this, the hero number reads as a hard
// "0 to final" jump even though the bar below it already animates smoothly.
// ---------------------------------------------------------------------------

function useAnimatedNumber(target: number, durationMs = 600): number {
  const [value, setValue] = useState(target);
  const fromRef = useRef(target);
  const startRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const prefersReducedMotion =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (prefersReducedMotion) {
      setValue(target);
      return;
    }

    fromRef.current = value;
    startRef.current = null;

    const tick = (now: number) => {
      if (startRef.current === null) startRef.current = now;
      const elapsed = now - startRef.current;
      const t = Math.min(1, elapsed / durationMs);
      // Ease-out cubic — fast start, gentle settle.
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(fromRef.current + (target - fromRef.current) * eased);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      }
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally
    // excludes `value`; re-running on every tick would restart the tween.
  }, [target, durationMs]);

  return value;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ScoreHero({
  score,
  tablesProfiled,
  totalTables,
  state = "complete",
  variant = "standard",
  trendDelta,
  title,
}: ScoreHeroProps) {
  // Must run before the early return below — hooks can't be called
  // conditionally, and this component does transition into and out of
  // the "empty" state as a profiling run starts.
  const animatedScore = useAnimatedNumber(score);

  // Empty state — hidden until first table profiled
  if (state === "empty") return null;

  const severity = severityFromScore(score);
  const config = SEVERITY_CONFIG[severity];
  const pct =
    totalTables > 0 ? Math.round((tablesProfiled / totalTables) * 100) : 0;
  // Round to whole number for the hero display — the aggregate is a raw
  // mean with full precision (e.g. 91.10444…) which reads as noise in a
  // 4xl display number. Keep full precision in the aria-label so screen
  // readers report the true value.
  const displayScore = Math.round(animatedScore);

  const ariaLabel = `Data Readiness Score: ${score}, ${config.label}. ${tablesProfiled} of ${totalTables} tables profiled.`;

  if (variant === "compact") {
    return (
      <div
        role="status"
        aria-live="polite"
        aria-label={ariaLabel}
        className="flex items-center gap-3"
      >
        <span
          className="text-heading-2 font-bold"
          style={{ color: config.color }}
          title={`Exact score: ${score.toFixed(2)}`}
        >
          {config.icon} {displayScore}
        </span>
        <span className="text-caption text-[var(--muted-foreground)]">
          {config.label} · {tablesProfiled}/{totalTables} tables
        </span>
        {state === "streaming" && (
          <Loader2 className="w-4 h-4 animate-spin text-[var(--primary)]" />
        )}
      </div>
    );
  }

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={ariaLabel}
      className="flex flex-col items-center text-center"
    >
      {/* Title — tells first-time users what the number represents */}
      {title && (
        <p className="text-body font-bold uppercase tracking-wider text-[var(--foreground)] mb-1">
          {title}
        </p>
      )}

      {/* Score number */}
      <div className="flex items-center gap-2">
        <span
          className={`font-bold ${variant === "stewardship" ? "text-display" : "text-display"}`}
          style={{ color: config.color }}
          title={`Exact score: ${score.toFixed(2)}`}
        >
          {displayScore}
        </span>
        {state === "streaming" && (
          <Loader2 className="w-5 h-5 animate-spin text-[var(--primary)]" />
        )}
        {state === "complete" && (
          <CheckCircle2 className="w-5 h-5" style={{ color: config.color }} />
        )}
        {state === "paused" && (
          <Pause className="w-5 h-5 text-[var(--muted-foreground)]" />
        )}
        {state === "error" && (
          <AlertCircle className="w-5 h-5 text-[var(--destructive)]" />
        )}
      </div>

      {/* Severity label */}
      <span
        className="text-body font-medium mt-1"
        style={{ color: config.color }}
      >
        {config.icon} {config.label}
      </span>

      {/* Stewardship trend */}
      {variant === "stewardship" &&
        trendDelta !== undefined &&
        trendDelta !== 0 && (
          <div className="flex items-center gap-1 mt-1 text-caption">
            {trendDelta > 0 ? (
              <TrendingUp className="w-3 h-3 text-[var(--readiness-excellent)]" />
            ) : (
              <TrendingDown className="w-3 h-3 text-[var(--destructive)]" />
            )}
            <span
              className={
                trendDelta > 0
                  ? "text-[var(--readiness-excellent)]"
                  : "text-[var(--destructive)]"
              }
            >
              {trendDelta > 0 ? "+" : ""}
              {trendDelta} from last run
            </span>
          </div>
        )}

      {/* Context string */}
      <p className="text-caption text-[var(--muted-foreground)] mt-2">
        {tablesProfiled} of {totalTables} tables profiled
      </p>

      {/* Progress bar */}
      <div className="w-48 h-1.5 rounded-full bg-[var(--muted-foreground)]/20 mt-2 overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-700 ease-out"
          style={{ width: `${pct}%`, backgroundColor: config.color }}
        />
      </div>
    </div>
  );
}
